(() => {
  'use strict';
  const initialized = new WeakSet();
  const filters = new WeakMap();
  const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  const roots = (scope) => [
    ...(scope.matches?.('[data-orso-consultation]') ? [scope] : []),
    ...scope.querySelectorAll('[data-orso-consultation]'),
  ];

  const productContexts = new WeakMap();
  const coverPreviews = new WeakMap();
  let activeProduct = null;
  let productDialogSequence = 0;
  function createProductDialog(card, { onClose, onOpen } = {}) {
    const details = card.querySelector('details');
    const summary = details.querySelector('summary');
    const body = card.querySelector('.orso-consultation__body');
    const dialog = document.createElement('dialog');
    const nativeDialog = typeof dialog.showModal === 'function' && typeof dialog.close === 'function';
    const title = card.querySelector('h3')?.textContent || 'Orso Club';
    const listeners = [];
    const listen = (node, name, handler) => {
      node.addEventListener(name, handler);
      listeners.push(() => node.removeEventListener(name, handler));
    };
    let closeButton;
    let disposed = false;
    let pendingRestoreFocus = true;
    let backdropPointerDown = false;
    const release = (restoreFocus) => {
      if (activeProduct !== api) return;
      activeProduct = null;
      onClose?.();
      document.body.classList.remove('orso-product-dialog-open');
      if (restoreFocus && summary.isConnected !== false) summary.focus({ preventScroll: true });
    };
    const api = {
      card,
      openProduct({ focusFirst = true } = {}) {
        if (disposed) return;
        coverPreviews.get(card)?.reset();
        if (activeProduct && activeProduct !== api) activeProduct.closeProduct({ restoreFocus: false });
        activeProduct = api;
        pendingRestoreFocus = true;
        if (nativeDialog) {
          details.open = false;
          document.body.classList.add('orso-product-dialog-open');
          if (!dialog.open) dialog.showModal();
          if (focusFirst) closeButton.focus({ preventScroll: true });
        } else {
          details.open = true;
        }
        onOpen?.();
      },
      closeProduct({ restoreFocus = true } = {}) {
        pendingRestoreFocus = restoreFocus;
        if (nativeDialog && dialog.open) dialog.close();
        details.open = false;
        release(restoreFocus);
      },
      destroyProduct() {
        api.closeProduct({ restoreFocus: false });
        disposed = true;
        listeners.forEach((remove) => remove());
        if (nativeDialog) {
          details.append(body);
          dialog.remove();
          summary.removeAttribute('aria-haspopup');
          summary.removeAttribute('aria-controls');
          card.classList.remove('orso-consultation__card--dialog');
        }
      },
    };
    if (nativeDialog) {
      const id = 'orso-product-dialog-' + (++productDialogSequence);
      const headingId = id + '-title';
      dialog.id = id;
      dialog.className = 'orso-consultation__product-dialog';
      dialog.setAttribute('aria-labelledby', headingId);
      const header = document.createElement('header');
      header.className = 'orso-consultation__dialog-header';
      const heading = document.createElement('h2');
      heading.id = headingId;
      heading.className = 'orso-consultation__dialog-title';
      heading.textContent = title;
      closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'orso-consultation__dialog-close';
      closeButton.textContent = 'Fechar';
      closeButton.setAttribute('aria-label', 'Fechar detalhes de ' + title);
      header.append(heading, closeButton);
      dialog.append(header, body);
      card.append(dialog);
      details.open = false;
      summary.setAttribute('aria-haspopup', 'dialog');
      summary.setAttribute('aria-controls', id);
      card.classList.add('orso-consultation__card--dialog');
      listen(summary, 'click', (event) => { event.preventDefault(); api.openProduct(); });
      listen(details, 'toggle', () => {
        if (details.open) { details.open = false; api.openProduct(); }
      });
      listen(closeButton, 'click', () => api.closeProduct());
      listen(dialog, 'cancel', (event) => { event.preventDefault(); api.closeProduct(); });
      listen(dialog, 'close', () => {
        if (dialog.open) return;
        release(pendingRestoreFocus);
        pendingRestoreFocus = true;
      });
      const outside = (event) => {
        if (event.target !== dialog || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return false;
        const rect = dialog.getBoundingClientRect();
        return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      };
      listen(dialog, 'pointerdown', (event) => { backdropPointerDown = outside(event); });
      listen(dialog, 'pointercancel', () => { backdropPointerDown = false; });
      listen(dialog, 'click', (event) => {
        if (backdropPointerDown && outside(event)) api.closeProduct();
        backdropPointerDown = false;
      });
    } else {
      listen(details, 'toggle', () => {
        if (details.open) {
          if (activeProduct && activeProduct !== api) activeProduct.closeProduct({ restoreFocus: false });
          activeProduct = api;
        } else release(false);
      });
    }
    return api;
  }

  const modelOption = (option) => option.name === 'Modelo' || option.name === 'Modelagem';
  const currentModel = (data, values) => {
    const index = data.options.findIndex(modelOption);
    return index >= 0 ? data.options[index].values[values[index]] : data.currentModel;
  };
  const photoState = (data, imageIndex, model) => {
    const evidence = data.imageModelEvidence?.[imageIndex] || 'unknown';
    const permitted = evidence === model || evidence === 'shared';
    return {
      permitted,
      illustrative: true,
      shared: evidence === 'shared',
      message: evidence === 'shared'
        ? 'Imagem ilustrativa da estampa; o caimento pode variar conforme a modelagem escolhida.'
        : permitted ? 'Imagem ilustrativa.' : 'Imagem indisponível para esta combinação.',
    };
  };
  const variantAt = (data, values) => data.variants.find((variant) => variant.o.every((value, i) => value === values[i]));
  const choiceSnapshot = (data, values) => Object.fromEntries(data.options.map((option, i) => [option.name, option.values[values[i]]]));
  function compatibleValues(data, wanted, candidates = data.variants) {
    if (!candidates.length) return null;
    const score = (variant) => data.options.reduce((sum, option, i) => sum + (wanted[option.name] === option.values[variant.o[i]] ? (option.name === 'Tamanho' ? 10 : 1) : 0), 0);
    const best = [...candidates].sort((a, b) => score(b) - score(a))[0];
    return { values: [...best.o], changed: data.options.filter((option, i) => wanted[option.name] !== undefined && wanted[option.name] !== option.values[best.o[i]]).map((option) => option.name) };
  }
  function valuesForImage(data, imageIndex, values) {
    const evidence = data.imageModelEvidence?.[imageIndex] || 'unknown';
    if (evidence === 'unknown') return null;
    const model = evidence === 'shared' ? currentModel(data, values) : evidence;
    const target = data.imageVariantTargets?.[imageIndex] ?? imageIndex;
    if (!Number.isInteger(target) || !data.images[target]) return null;
    const candidates = data.variants.filter((variant) => variant.i === target && currentModel(data, variant.o) === model);
    return compatibleValues(data, choiceSnapshot(data, values), candidates)?.values || null;
  }
  function defaultCoverChoice(data, values, preferredColor) {
    const colorIndex = data.options.findIndex((option) => option.name === 'Cor');
    const colors = data.options[colorIndex]?.values || [];
    const preferredIndex = colors.findIndex((color) => color.toLowerCase() === preferredColor.toLowerCase());
    const candidates = colorIndex < 0 ? [null] : [...new Set([preferredIndex, values[colorIndex]])].filter((index) => index >= 0);
    for (const color of candidates) {
      const candidate = [...values];
      if (colorIndex >= 0) candidate[colorIndex] = color;
      const variant = variantAt(data, candidate);
      if (!variant || !data.images[variant.i] || !photoState(data, variant.i, currentModel(data, candidate)).permitted) continue;
      const actualColor = colorIndex < 0 ? null : colors[color];
      return { values: candidate, color: actualColor, fallback: actualColor !== preferredColor };
    }
    return null;
  }
  const imageView = (data, index) => ['Frente', 'Costas'].includes(data.imageViews?.[index]) ? data.imageViews[index] : '';
  let optionControlSequence = 0;


  // Shopify CDN renditions keep the same approved image and let the browser pick its size.
  const imageProfiles = {
    cover: { width: 600, widths: [240, 360, 480, 600, 720, 900, 1200], sizes: '(min-width: 1600px) calc((100vw - 160px) / 5), (min-width: 1200px) calc((95vw - 80px) / 5), (min-width: 761px) calc((95vw - 36px) / 3), calc((100vw - 44px) / 2)' },
    detail: { width: 900, widths: [360, 600, 900, 1200, 1600], sizes: '(min-width: 880px) 800px, (min-width: 761px) calc(100vw - 80px), calc(100vw - 52px)' },
    swatch: { width: 128, widths: [64, 128, 192], sizes: '64px' },
    thumbnail: { width: 144, widths: [72, 144, 216], sizes: '72px' },
  };
  function imageRenditions(url, kind) {
    const profile = imageProfiles[kind] || imageProfiles.cover;
    let source;
    try { source = new URL(url, window.location.href); } catch (_) { return null; }
    if (!['https:', 'http:'].includes(source.protocol)) return null;
    const shopifyCDN = /(^|\.)cdn\.shopify\.com$|(^|\.)shopifycdn\.net$/.test(source.hostname)
      || (source.origin === window.location.origin && source.pathname.startsWith('/cdn/shop/'));
    if (!shopifyCDN) return { src: source.href, srcset: '', sizes: '' };
    const resized = (width) => { const rendition = new URL(source.href); rendition.searchParams.set('width', String(width)); return rendition.href; };
    return { src: resized(profile.width), srcset: profile.widths.map((width) => resized(width) + ' ' + width + 'w').join(', '), sizes: profile.sizes };
  }
  function setProductImage(image, url, kind) {
    if (!image || !url) return;
    const rendition = imageRenditions(url, kind);
    if (!rendition) return;
    if (rendition.srcset) {
      if (image.getAttribute('sizes') !== rendition.sizes) image.setAttribute('sizes', rendition.sizes);
      if (image.getAttribute('srcset') !== rendition.srcset) image.setAttribute('srcset', rendition.srcset);
    } else {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
    }
    if (image.getAttribute('src') !== rendition.src) image.src = rendition.src;
    image.decoding = 'async';
  }
  // A card preview is visual only: it never writes selected option values.
  function secondaryPreviewIndex(data, primary, values) {
    const model = currentModel(data, values);
    if (!data.images[primary] || !photoState(data, primary, model).permitted) return null;
    const sameCombination = data.images.findIndex((url, index) => index !== primary
      && (data.imageVariantTargets?.[index] ?? index) === primary
      && photoState(data, index, model).permitted);
    if (sameCombination >= 0) return sameCombination;
    const colorIndex = data.options.findIndex((option) => option.name === 'Cor');
    if (colorIndex < 0) return null;
    const alternate = data.variants.find((variant) => variant.i !== primary
      && variant.o[colorIndex] !== values[colorIndex]
      && variant.o.every((value, index) => index === colorIndex || value === values[index])
      && data.images[variant.i] && photoState(data, variant.i, model).permitted);
    return alternate ? alternate.i : null;
  }
  function createCoverPreview(card, data) {
    const cover = card.querySelector('.orso-consultation__cover');
    const image = cover?.querySelector('img');
    const summary = card.querySelector('summary');
    if (!cover || !image || !summary) return null;
    const title = card.querySelector('h3')?.textContent || 'Orso Club';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'orso-consultation__preview-toggle';
    button.textContent = 'Outra foto';
    button.setAttribute('aria-label', 'Ver outra foto de ' + title);
    button.setAttribute('aria-pressed', 'false');
    button.hidden = true;
    cover.append(button);
    let primary = data.variants[0].i;
    let secondary = null;
    let snapshot = null;
    let showing = false;
    let hovered = false;
    let focused = false;
    let toggled = false;
    let disposed = false;
    const listeners = [];
    const listen = (node, name, handler) => {
      node.addEventListener(name, handler);
      listeners.push(() => node.removeEventListener(name, handler));
    };
    const render = () => {
      const next = !disposed && secondary !== null && !image.hidden && (hovered || focused || toggled);
      if (next !== showing) {
        if (next) {
          setProductImage(image, data.images[secondary], 'cover');
          image.alt = title + ' - outra foto' + (imageView(data, secondary) ? ' - ' + imageView(data, secondary).toLowerCase() : '');
          cover.setAttribute('data-orso-preview-active', 'true');
        } else if (snapshot) {
          for (const name of ['src', 'srcset', 'sizes', 'alt']) {
            if (snapshot[name] === null) image.removeAttribute(name);
            else if (image.getAttribute(name) !== snapshot[name]) image.setAttribute(name, snapshot[name]);
          }
          cover.removeAttribute('data-orso-preview-active');
        }
        showing = next;
      }
      button.setAttribute('aria-pressed', next ? 'true' : 'false');
      button.textContent = next ? 'Foto principal' : 'Outra foto';
      button.setAttribute('aria-label', (next ? 'Ver foto principal de ' : 'Ver outra foto de ') + title);
    };
    const api = {
      update(index, values) {
        primary = index;
        secondary = secondaryPreviewIndex(data, primary, values);
        // updateCover has already written the selected cover. Never restore stale bytes.
        showing = false;
        hovered = false; focused = false; toggled = false;
        snapshot = Object.fromEntries(['src', 'srcset', 'sizes', 'alt'].map((name) => [name, image.getAttribute(name)]));
        cover.removeAttribute('data-orso-preview-active');
        button.hidden = secondary === null || image.hidden;
        render();
      },
      reset() { hovered = false; focused = false; toggled = false; render(); },
      destroy() {
        api.reset(); disposed = true;
        listeners.forEach((remove) => remove());
        button.remove(); coverPreviews.delete(card);
      },
    };
    listen(cover, 'pointerenter', (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      hovered = true; render();
    });
    listen(cover, 'pointerleave', () => { hovered = false; render(); });
    listen(summary, 'focusin', (event) => { if (event.target === summary) { focused = true; render(); } });
    listen(summary, 'focusout', (event) => {
      if (event.target === summary || event.target === button) { focused = false; toggled = false; render(); }
    });
    listen(button, 'click', (event) => {
      event.preventDefault(); event.stopPropagation?.();
      focused = false; toggled = !toggled; render();
    });
    coverPreviews.set(card, api);
    api.update(primary, data.variants[0].o);
    return api;
  }
  function updateCover(card, data, values) {
    const variant = variantAt(data, values);
    const cover = card.querySelector('.orso-consultation__cover img');
    if (!variant || !cover) return;
    const coverState = photoState(data, variant.i, currentModel(data, values));
    cover.hidden = !coverState.permitted || !data.images[variant.i];
    if (!cover.hidden) {
      setProductImage(cover, data.images[variant.i], 'cover');
      const title = card.querySelector('h3')?.textContent || 'Orso Club';
      const colorIndex = data.options.findIndex((option) => option.name === 'Cor');
      const color = colorIndex < 0 ? '' : data.options[colorIndex].values[values[colorIndex]];
      cover.alt = title + ' - camiseta' + (color ? ' ' + color.toLowerCase() : '') + ' - imagem ilustrativa';
    }
    coverPreviews.get(card)?.update(variant.i, values);
    card.querySelectorAll('[data-orso-price]').forEach((price) => { price.textContent = money.format(Number(variant.p)); });
  }
  // The grid needs only cover state. Build selectors, thumb handlers and dialog on first open.
  function createCardState(card, context) {
    const data = JSON.parse(card.querySelector('[data-orso-product-data]').textContent);
    const details = card.querySelector('details');
    const summary = details.querySelector('summary');
    let values = [...data.variants[0].o];
    let explicitChoice = false;
    let transferredMessage = '';
    let enhanced = null;
    let disposed = false;
    const preview = createCoverPreview(card, data);
    const removeOpenListeners = () => {
      summary.removeEventListener('click', openOnClick);
      details.removeEventListener('toggle', openOnToggle);
    };
    const ensure = () => {
      if (disposed) return null;
      if (!enhanced) {
        enhanced = enhanceCard(card, context, { data, values, explicitChoice, transferredMessage });
        removeOpenListeners();
      }
      return enhanced;
    };
    function openOnClick(event) { event.preventDefault(); ensure()?.openProduct(); }
    function openOnToggle() { if (details.open && !enhanced) ensure()?.openProduct(); }
    summary.addEventListener('click', openOnClick);
    details.addEventListener('toggle', openOnToggle);
    return {
      card,
      openProduct(options) { ensure()?.openProduct(options); },
      closeProduct(options) { enhanced?.closeProduct(options); },
      destroyProduct() { disposed = true; removeOpenListeners(); preview?.destroy(); enhanced?.destroyProduct(); },
      applyDefaultCover(preferredColor) {
        if (enhanced) return enhanced.applyDefaultCover(preferredColor);
        if (explicitChoice) return { applied: false, reason: 'explicit-choice' };
        const resolved = defaultCoverChoice(data, values, preferredColor);
        if (!resolved) return { applied: false, reason: 'no-compatible-image' };
        values = resolved.values;
        updateCover(card, data, values);
        return { applied: true, color: resolved.color, fallback: resolved.fallback };
      },
      accept(wanted) {
        if (enhanced) return enhanced.accept(wanted);
        const resolved = compatibleValues(data, wanted);
        if (!resolved) return;
        explicitChoice = true;
        values = resolved.values;
        transferredMessage = resolved.changed.length ? 'Opções ajustadas para uma combinação existente: ' + resolved.changed.join(', ') : '';
        updateCover(card, data, values);
      },
      focusModel() { ensure()?.focusModel(); },
    };
  }

  function enhanceCard(card, context, seed) {
    const data = seed?.data || JSON.parse(card.querySelector('[data-orso-product-data]').textContent);
    const holder = card.querySelector('[data-orso-options]');
    const main = card.querySelector('.orso-consultation__main img');
    const cover = card.querySelector('.orso-consultation__cover img');
    const figure = main.closest('figure');
    const caption = card.querySelector('[data-orso-image-caption]');
    const selection = card.querySelector('[data-orso-selection]');
    const thumbs = [...card.querySelectorAll('[data-orso-thumb]')];
    const viewLabels = thumbs.map((thumb, index) => {
      const view = imageView(data, index);
      if (!view) return null;
      const label = document.createElement('span');
      label.className = 'orso-consultation__view-label';
      label.textContent = view;
      thumb.append(label);
      return label;
    });
    const title = card.querySelector('h3')?.textContent || 'Orso Club';
    let values = [...(seed?.values || data.variants[0].o)];
    let transferredMessage = seed?.transferredMessage || '';
    let explicitChoice = seed?.explicitChoice || false;
    const modelHolder = document.createElement('div');
    modelHolder.className = 'orso-consultation__model-control';
    const warning = document.createElement('p');
    warning.className = 'orso-consultation__photo-note';
    warning.setAttribute('role', 'status');
    warning.setAttribute('aria-live', 'polite');
    figure.append(warning);
    const hasNativeModel = data.options.some(modelOption);
    const hasModelPeers = !hasNativeModel && data.currentModel && data.modelPeerHandles;
    if (hasNativeModel || hasModelPeers) figure.before(modelHolder);
    const controls = [];
    let commerce;
    let virtualModelControl;
    const chooseImage = (index) => {
      const url = data.images[index];
      if (!url) return;
      const model = currentModel(data, values);
      const state = photoState(data, index, model);
      const variant = variantAt(data, values);
      main.hidden = !state.permitted;
      if (state.permitted) {
        setProductImage(main, url, 'detail');
        main.alt = title + ' - imagem ilustrativa' + (imageView(data, index) ? ' - ' + imageView(data, index).toLowerCase() : '');
      }
      caption.textContent = state.permitted ? imageView(data, index) || 'Imagem' : 'Imagem indisponível';
      warning.textContent = state.message;
      warning.hidden = !state.message;
      thumbs.forEach((thumb, i) => {
        const target = data.imageVariantTargets?.[i] ?? i;
        const matching = variant && target === variant.i && photoState(data, i, model).permitted;
        thumb.hidden = !matching;
        const view = imageView(data, i);
        thumb.setAttribute('aria-label', view ? 'Ver ' + view.toLowerCase() : 'Ver imagem ' + (i + 1));
        const thumbnailImage = thumb.querySelector('img');
        if (thumbnailImage) {
          thumbnailImage.alt = title + ' - ' + (view ? view.toLowerCase() : 'imagem ilustrativa');
          if (matching) setProductImage(thumbnailImage, data.images[i], 'thumbnail');
        }
        if (i === index && state.permitted && matching) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    };
    const removeThumbListeners = [];
    thumbs.forEach((thumb) => {
      const onThumbClick = (event) => {
      event.preventDefault();
      if (thumb.hidden) return;
      const proposed = valuesForImage(data, Number(thumb.dataset.orsoThumb), values);
      if (!proposed) {
        selection.textContent = 'Essa imagem não está associada a uma combinação existente.';
        return;
      }
      explicitChoice = true;
      values = proposed;
      update();
      chooseImage(Number(thumb.dataset.orsoThumb));
      };
      thumb.addEventListener('click', onThumbClick);
      removeThumbListeners.push(() => thumb.removeEventListener('click', onThumbClick));
    });
    function makeControl(option, index, target, onChange) {
      const displayName = modelOption(option) ? 'Modelagem' : option.name;
      const isButtons = modelOption(option) || option.name === 'Cor' || option.name === 'Tamanho';
      let element, select, inputs = [], images = [];
      if (isButtons) {
        element = document.createElement('fieldset');
        element.className = 'orso-consultation__choice-group';
        const legend = document.createElement('legend');
        legend.textContent = displayName;
        element.append(legend);
        const choices = document.createElement('div');
        choices.className = 'orso-consultation__choices';
        const groupName = 'orso-choice-' + (++optionControlSequence);
        option.values.forEach((value, j) => {
          const label = document.createElement('label');
          label.className = 'orso-consultation__choice' + (option.name === 'Cor' ? ' orso-consultation__choice--color' : '');
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = groupName;
          input.value = String(j);
          input.checked = j === (index >= 0 ? values[index] : option.values.indexOf(data.currentModel));
          const visual = document.createElement('span');
          visual.className = 'orso-consultation__choice-visual';
          if (option.name === 'Cor') {
            const image = document.createElement('img');
            image.width = 64; image.height = 72; image.loading = 'lazy'; image.alt = '';
            image.hidden = true;
            visual.append(image);
            images[j] = image;
          }
          const text = document.createElement('span');
          text.textContent = value;
          visual.append(text);
          label.append(input, visual);
          input.addEventListener('click', () => { explicitChoice = true; });
          input.addEventListener('change', () => { if (input.checked) { explicitChoice = true; onChange(j); } });
          inputs.push(input);
          choices.append(label);
        });
        element.append(choices);
      } else {
        element = document.createElement('label');
        const text = document.createElement('span');
        text.textContent = displayName;
        select = document.createElement('select');
        option.values.forEach((value, j) => {
          const item = document.createElement('option');
          item.value = String(j); item.textContent = value; item.selected = j === values[index];
          select.append(item);
        });
        select.addEventListener('change', () => { explicitChoice = true; onChange(Number(select.value)); });
        element.append(text, select);
      }
      target.append(element);
      return {
        option, index, images,
        set(value) { if (select) select.value = String(value); inputs.forEach((input, j) => { input.checked = j === value; }); },
        focus() { (select || inputs.find((input) => input.checked) || inputs[0])?.focus({ preventScroll: true }); },
      };
    }
    function updateColorThumbnails() {
      controls.filter((control) => control.option.name === 'Cor').forEach((control) => {
        control.option.values.forEach((color, j) => {
          const candidateValues = [...values]; candidateValues[control.index] = j;
          const variant = variantAt(data, candidateValues);
          const image = control.images[j];
          if (!image) return;
          const state = variant && photoState(data, variant.i, currentModel(data, values));
          image.hidden = !state?.permitted;
          if (state?.permitted) {
            setProductImage(image, data.images[variant.i], 'swatch');
            image.alt = color + ' - imagem ilustrativa';
          }
        });
      });
    }
    const update = () => {
      const variant = variantAt(data, values);
      if (!variant) {
        main.hidden = true;
        selection.textContent = 'Essa combinação não faz parte deste modelo. Escolha outras opções.';
        commerce?.update(null);
        return;
      }
      controls.forEach((control) => control.set(values[control.index]));
      chooseImage(variant.i);
      updateCover(card, data, values);
      updateColorThumbnails();
      commerce?.update(variant);
      const parts = data.options.map((option, i) => (modelOption(option) ? 'Modelagem' : option.name) + ': ' + option.values[values[i]]);
      if (hasModelPeers) parts.unshift('Modelagem: ' + data.currentModel);
      selection.textContent = parts.join(' · ') + (transferredMessage ? '. ' + transferredMessage : '');
      transferredMessage = '';
    };
    data.options.forEach((option, i) => {
      controls.push(makeControl(option, i, modelOption(option) ? modelHolder : holder, (value) => { values[i] = value; update(); }));
    });
    if (hasModelPeers) {
      const models = { name: 'Modelagem', values: ['Masculina', 'Feminina'] };
      virtualModelControl = makeControl(models, -1, modelHolder, (j) => {
        const model = models.values[j];
        const handle = data.modelPeerHandles[model];
        const destination = context.states.get('orso-product-' + handle);
        if (!destination) {
          virtualModelControl.set(models.values.indexOf(data.currentModel));
          selection.textContent = 'A outra modelagem ainda não está disponível nesta consulta.';
          return;
        }
        const saved = choiceSnapshot(data, values);
        destination.accept(saved);
        if (destination.card !== card) {
          productDialog.closeProduct({ restoreFocus: false });
          destination.openProduct({ focusFirst: false });
        }
        destination.focusModel();
        virtualModelControl.set(models.values.indexOf(data.currentModel));
      });
    }
    // Keep the chosen options and their purchase action ahead of the long description.
    const description = card.querySelector('.orso-consultation__description');
    if (description && holder?.parentNode === description.parentNode && selection?.parentNode === description.parentNode) {
      description.before(holder, selection);
    }
    commerce = window.OrsoGalleryCommerce?.mount({ card, data, title, isActive: () => activeProduct?.card === card });
    update();
    card.classList.add('orso-consultation__card--enhanced');
    const productDialog = createProductDialog(card, { onClose: () => commerce?.close(), onOpen: () => commerce?.open() });
    return {
      card,
      openProduct(options) { productDialog.openProduct(options); },
      closeProduct(options) { productDialog.closeProduct(options); },
      destroyProduct() {
        commerce?.destroy();
        productDialog.destroyProduct();
        removeThumbListeners.forEach((remove) => remove());
        viewLabels.forEach((label) => label?.remove());
        holder.replaceChildren();
        modelHolder.remove();
        warning.remove();
        card.classList.remove('orso-consultation__card--enhanced');
      },
      applyDefaultCover(preferredColor) {
        if (explicitChoice) return { applied: false, reason: 'explicit-choice' };
        if (activeProduct?.card === card) return { applied: false, reason: 'dialog-open' };
        const resolved = defaultCoverChoice(data, values, preferredColor);
        if (!resolved) return { applied: false, reason: 'no-compatible-image' };
        values = resolved.values;
        update();
        return { applied: true, color: resolved.color, fallback: resolved.fallback };
      },
      accept(wanted) {
        explicitChoice = true;
        const resolved = compatibleValues(data, wanted);
        values = resolved.values;
        transferredMessage = resolved.changed.length ? 'Opções ajustadas para uma combinação existente: ' + resolved.changed.join(', ') : '';
        update();
      },
      focusModel() { (virtualModelControl || controls.find((control) => modelOption(control.option)))?.focus(); },
    };
  }
  function initialize(scope) {
    roots(scope).forEach((root) => {
      if (initialized.has(root)) return;
      const cards = [...root.querySelectorAll('[data-orso-card]')];
      const context = { states: new Map(), removeRootListeners: [] };
      productContexts.set(root, context);
      cards.forEach((card) => {
        try { context.states.set(card.id, createCardState(card, context)); } catch (_) {}
      });
      const club = root.querySelector('[data-orso-club-filter]');
      const count = root.querySelector('[data-orso-count]');
      const empty = root.querySelector('[data-orso-empty]');
      const reset = root.querySelector('[data-orso-reset]');
      const links = [...root.querySelectorAll('[data-orso-club]')];
      const apply = () => {
        const selectedClub = club?.value || '';
        let visible = 0;
        cards.forEach((card) => {
          coverPreviews.get(card)?.reset();
          const matches = !selectedClub || (card.dataset.orsoClubs || '').split(' ').includes(selectedClub);
          card.hidden = !matches;
          if (matches) visible += 1;
        });
        [...root.querySelectorAll('[data-orso-card]')].filter((card) => !card.hidden).forEach((card, index) => {
          context.states.get(card.id)?.applyDefaultCover(index % 2 === 0 ? 'Branco' : 'Preto');
        });
        if (activeProduct && cards.includes(activeProduct.card) && activeProduct.card.hidden) {
          activeProduct.closeProduct({ restoreFocus: false });
          const filterFocus = window.matchMedia?.('(max-width: 760px)')?.matches
            ? club : links.find((link) => link.dataset.orsoClub === selectedClub) || links[0] || club;
          filterFocus?.focus({ preventScroll: true });
        }
        if (count) count.textContent = `${visible} ${visible === 1 ? 'modelo' : 'modelos'}`;
        if (empty) empty.hidden = visible !== 0;
        if (reset) reset.hidden = !selectedClub;
        links.forEach((link) => {
          if (link.dataset.orsoClub === selectedClub) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      };
      const applyHash = (hash = window.location.hash.slice(1)) => {
        if (!club) return false;
        if (!hash || hash === 'orso-catalog') club.value = '';
        else if ([...club.options].some((option) => option.value && option.value === hash)) club.value = hash;
        else return false;
        apply();
        return true;
      };
      club?.addEventListener('change', apply);
      context.removeRootListeners.push(() => club?.removeEventListener('change', apply));
      const resetFilters = () => {
        if (club) club.value = '';
        apply();
        const allLink = links.find((link) => link.dataset.orsoClub === '');
        if (window.matchMedia?.('(max-width: 760px)')?.matches && club) club.focus();
        else allLink?.focus();
      };
      reset?.addEventListener('click', resetFilters);
      context.removeRootListeners.push(() => reset?.removeEventListener('click', resetFilters));
      const controls = root.querySelector('[data-orso-controls]');
      if (controls) controls.hidden = false;
      initialized.add(root);
      filters.set(root, applyHash);
      if (!applyHash()) apply();
    });
  }
  function cleanupProducts(scope) {
    roots(scope).forEach((root) => {
      const context = productContexts.get(root);
      context?.states.forEach((state) => state.destroyProduct());
      context?.removeRootListeners.forEach((remove) => remove());
      productContexts.delete(root);
      filters.delete(root);
      initialized.delete(root);
    });
  }
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupProducts(event.target));
  window.addEventListener('hashchange', () => roots(document).forEach((root) => filters.get(root)?.()));
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    const anchor = event.target.closest?.('a[href]');
    if (!anchor) return;
    const destination = new URL(anchor.href, window.location.href);
    if (destination.origin !== window.location.origin || destination.pathname !== window.location.pathname || destination.search !== window.location.search) return;
    const hash = destination.hash.slice(1);
    roots(document).forEach((root) => filters.get(root)?.(hash));
  });
})();
